import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Factory, HardHat, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1613665813446-82a78c468a1d?w=1920&q=85" 
            alt="Solar Park" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a8a]/90 to-[#1e3a8a]/70"></div>
        </div>
        
        {/* Animated Background Circles */}
        <div className="absolute inset-0 z-5 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#fbbf24]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#fbbf24]/15 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
        
        {/* B Logo Watermark */}
        <div className="absolute bottom-10 right-10 opacity-10 z-10 animate-pulse">
          <img 
            src="https://customer-assets.emergentagent.com/job_b5d7f5d1-8fe3-4df9-bbe6-0522c761a4fd/artifacts/prqfh8ph_WhatsApp%20Image%202026-02-07%20at%2022.24.51.jpeg" 
            alt="BRISE Logo" 
            className="w-96 h-96 object-contain"
          />
        </div>
        
        {/* Floating Solar Panel Icons */}
        <div className="absolute inset-0 z-5 pointer-events-none">
          <div className="absolute top-32 right-1/4 text-[#fbbf24]/30 animate-bounce" style={{animationDuration: '3s'}}>
            <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
              <rect x="4" y="4" width="6" height="6" />
              <rect x="14" y="4" width="6" height="6" />
              <rect x="4" y="14" width="6" height="6" />
              <rect x="14" y="14" width="6" height="6" />
            </svg>
          </div>
          <div className="absolute bottom-40 left-1/4 text-[#fbbf24]/20 animate-bounce" style={{animationDuration: '4s', animationDelay: '0.5s'}}>
            <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8" />
              <path d="M12 2 L12 6 M12 18 L12 22 M2 12 L6 12 M18 12 L22 12 M5 5 L8 8 M16 16 L19 19 M5 19 L8 16 M16 8 L19 5" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
        </div>
        
        <div className="relative z-20 max-w-7xl mx-auto px-6 text-center text-white">
          <h1 className="text-7xl font-bold mb-6 leading-tight animate-fade-in">
            Energizing Bharat.<br />Empowering the Future.
          </h1>
          <p className="text-2xl mb-10 text-gray-100 animate-fade-in" style={{animationDelay: '0.2s'}}>
            Premier Manufacturer & EPC Partner for Solar Infrastructure
          </p>
          <Button 
            size="lg" 
            className="bg-[#fbbf24] hover:bg-[#f59e0b] text-[#1e3a8a] font-semibold px-10 py-7 text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl animate-fade-in"
            style={{animationDelay: '0.4s'}}
          >
            Discover Our Solutions
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 pattern-dots opacity-50"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="animate-slide-in-left">
              <h2 className="text-5xl font-bold text-[#1e3a8a] mb-6">
                Your Trusted Partner in Renewable Energy
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                BRISE (Bharat Renewable Infrastructure & Solar Energy Pvt. Ltd.) is a comprehensive 
                ecosystem for clean energy. From precision manufacturing of photovoltaic modules to 
                large-scale execution of power plants, we bridge the gap between sustainability and 
                industrial progress.
              </p>
              <Link to="/about">
                <Button variant="outline" className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all duration-300 hover:shadow-lg">
                  Read More About Us
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative animate-slide-in-right">
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#fbbf24]/20 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#1e3a8a]/10 rounded-full blur-2xl"></div>
              
              <img 
                src="https://images.unsplash.com/photo-1668097613572-40b7c11c8727?w=800&q=85" 
                alt="Solar Installation" 
                className="rounded-2xl shadow-2xl relative z-10 transform hover:scale-105 transition-transform duration-300"
              />
              
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-[#fbbf24] text-[#1e3a8a] px-6 py-3 rounded-xl shadow-lg z-20 animate-float">
                <div className="text-2xl font-bold">500+</div>
                <div className="text-sm font-semibold">MW Installed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Competencies Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center text-[#1e3a8a] mb-16">
            Core Competencies
          </h2>
          
          <div className="grid md:grid-cols-3 gap-12">
            {/* Manufacturing */}
            <div className="bg-white p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-20 h-20 bg-[#fbbf24] rounded-xl flex items-center justify-center mb-6">
                <Factory className="h-10 w-10 text-[#1e3a8a]" />
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Manufacturing</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                High-efficiency PV modules, inverters, and mounting structures built for durability.
              </p>
            </div>

            {/* EPC Projects */}
            <div className="bg-white p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-20 h-20 bg-[#fbbf24] rounded-xl flex items-center justify-center mb-6">
                <HardHat className="h-10 w-10 text-[#1e3a8a]" />
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">EPC Projects</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                End-to-end engineering, procurement, and construction for utility-scale and rooftop projects.
              </p>
            </div>

            {/* Operations */}
            <div className="bg-white p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-20 h-20 bg-[#fbbf24] rounded-xl flex items-center justify-center mb-6">
                <Settings className="h-10 w-10 text-[#1e3a8a]" />
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Operations (O&M)</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                24/7 monitoring and maintenance to ensure your assets perform at peak efficiency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BRISE Full Form Explanation Section */}
      <section className="py-24 px-6 bg-white relative overflow-hidden">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 pattern-grid opacity-30"></div>
        
        {/* Decorative circles */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#fbbf24]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#1e3a8a]/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1e3a8a] mb-6 animate-fade-in">What Does BRISE Mean?</h2>
            <p className="text-xl text-gray-700 animate-fade-in" style={{animationDelay: '0.2s'}}>A name that reflects our mission and vision</p>
          </div>

          <div className="grid md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {/* B */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group">
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">B</div>
              <div className="text-white text-lg font-semibold relative z-10">BHARAT</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">India</div>
            </div>

            {/* R */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group" style={{animationDelay: '0.1s'}}>
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">R</div>
              <div className="text-white text-lg font-semibold relative z-10">RENEWABLE</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">Sustainable</div>
            </div>

            {/* I */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group" style={{animationDelay: '0.2s'}}>
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">I</div>
              <div className="text-white text-lg font-semibold relative z-10">INFRASTRUCTURE</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">Foundation</div>
            </div>

            {/* S */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group" style={{animationDelay: '0.3s'}}>
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">S</div>
              <div className="text-white text-lg font-semibold relative z-10">SOLAR</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">Clean Power</div>
            </div>

            {/* E */}
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] p-8 rounded-2xl text-center transform hover:scale-110 hover:-rotate-2 transition-all duration-300 shadow-lg hover:shadow-2xl relative group" style={{animationDelay: '0.4s'}}>
              <div className="absolute inset-0 bg-[#fbbf24]/0 group-hover:bg-[#fbbf24]/10 rounded-2xl transition-all duration-300"></div>
              <div className="text-6xl font-bold text-[#fbbf24] mb-4 relative z-10">E</div>
              <div className="text-white text-lg font-semibold relative z-10">ENERGY</div>
              <div className="text-gray-200 text-sm mt-2 relative z-10">Future</div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-2xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
              Each letter represents our commitment to building a <span className="text-[#fbbf24] font-bold">self-reliant</span>, 
              <span className="text-[#1e3a8a] font-bold"> sustainable energy future</span> for India
            </p>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="p-8">
              <div className="text-5xl font-bold text-[#fbbf24] mb-3">500+</div>
              <p className="text-xl text-gray-700 font-semibold">MW Installed</p>
              <p className="text-gray-600 mt-2">Across India</p>
            </div>
            <div className="p-8">
              <div className="text-5xl font-bold text-[#fbbf24] mb-3">100+</div>
              <p className="text-xl text-gray-700 font-semibold">Projects Completed</p>
              <p className="text-gray-600 mt-2">Government & Private</p>
            </div>
            <div className="p-8">
              <div className="text-5xl font-bold text-[#fbbf24] mb-3">15+</div>
              <p className="text-xl text-gray-700 font-semibold">States Covered</p>
              <p className="text-gray-600 mt-2">Pan-India Presence</p>
            </div>
            <div className="p-8">
              <div className="text-5xl font-bold text-[#fbbf24] mb-3">24/7</div>
              <p className="text-xl text-gray-700 font-semibold">Support</p>
              <p className="text-gray-600 mt-2">Operations & Maintenance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Excellence Section - BRISE Branded */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1e3a8a]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#fbbf24]/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1e3a8a] mb-6">Manufacturing Excellence</h2>
            <p className="text-xl text-gray-700">Where precision meets sustainability</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image with BRISE branding overlay */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800&q=85" 
                  alt="BRISE Manufacturing Facility" 
                  className="w-full h-[500px] object-cover"
                />
                
                {/* BRISE Overlay Badge */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/90 via-transparent to-transparent"></div>
                
                {/* BRISE Logo Overlay */}
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-2xl">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] rounded-lg flex items-center justify-center">
                        <span className="text-2xl font-bold text-[#fbbf24]">B</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#1e3a8a]">BRISE</h3>
                        <p className="text-sm text-gray-600">Manufacturing Team</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Our certified engineers and technicians ensure world-class quality at every step
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <h3 className="text-3xl font-bold text-[#1e3a8a] mb-6">ISO-Certified Production</h3>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Our state-of-the-art manufacturing facilities are equipped with cutting-edge technology 
                and staffed by highly trained professionals who embody the BRISE commitment to excellence.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-[#fbbf24] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#1e3a8a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1e3a8a] mb-2">Quality Assurance</h4>
                    <p className="text-gray-600">Rigorous testing at every production stage</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-[#fbbf24] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#1e3a8a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1e3a8a] mb-2">Expert Team</h4>
                    <p className="text-gray-600">50+ certified engineers and technicians</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-[#fbbf24] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-[#1e3a8a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1e3a8a] mb-2">Advanced Technology</h4>
                    <p className="text-gray-600">Latest automation and precision equipment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1589201529153-5297335c1684?w=1920&q=85" 
            alt="Solar Field" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1e3a8a]/85"></div>
        </div>
        
        {/* Decorative circles */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-[#fbbf24]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-[#fbbf24]/20 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <h2 className="text-5xl font-bold mb-6">
            Ready to Power Your Future with Clean Energy?
          </h2>
          <p className="text-xl mb-10 text-gray-100">
            Let's discuss how BRISE can transform your energy infrastructure
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-[#fbbf24] hover:bg-[#f59e0b] text-[#1e3a8a] font-semibold px-10 py-7 text-lg transition-all duration-300 transform hover:scale-105"
            >
              Get a Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
